// @ts-check

// Input types that support the text-selection API; reading selectionStart on
// the others (email, number, …) returns null or throws, so they are not guarded.
const GUARDABLE_INPUT_TYPES = new Set(["text", "search", "url", "tel", "password"]);

const installedDocuments = new WeakSet();

/**
 * @param {Event} event
 * @returns {HTMLInputElement | HTMLTextAreaElement | null}
 */
function guardableTarget(event) {
  // composedPath (not event.target) so a pointerdown inside an open shadow
  // root resolves to the real inner text control, not the retargeted host.
  const target = event.composedPath()[0];
  if (target instanceof HTMLTextAreaElement) {
    return target;
  }
  if (target instanceof HTMLInputElement && GUARDABLE_INPUT_TYPES.has(target.type)) {
    return target;
  }
  return null;
}

/**
 * Chrome and Safari clear a text control's selection when a live mouse drag
 * hit-tests a node in a different shadow tree scope (Firefox 142+ is
 * unaffected). While a primary-button mouse drag started in a text control is
 * active, restore the last non-collapsed selection whenever the engine
 * collapses it mid-drag. The `selectionchange` listener must be on the
 * document — Chrome never fires the element-scoped event for shadow-nested
 * text controls.
 *
 * @param {Document} doc
 */
export function installSelectionDragGuard(doc = document) {
  if (installedDocuments.has(doc)) {
    return;
  }
  installedDocuments.add(doc);

  /** @type {HTMLInputElement | HTMLTextAreaElement | null} */
  let target = null;
  /** @type {{ start: number, end: number, direction: "forward" | "backward" | "none" } | null} */
  let saved = null;

  function endDrag() {
    target = null;
    saved = null;
  }

  doc.addEventListener(
    "pointerdown",
    (event) => {
      saved = null;
      if (event.button !== 0 || (event.pointerType && event.pointerType !== "mouse")) {
        target = null;
        return;
      }
      target = guardableTarget(event);
    },
    { capture: true },
  );
  doc.addEventListener("pointerup", endDrag, { capture: true });
  doc.addEventListener("pointercancel", endDrag, { capture: true });

  doc.addEventListener("selectionchange", () => {
    if (!target) {
      return;
    }

    if (target.selectionStart !== target.selectionEnd) {
      saved = {
        start: /** @type {number} */ (target.selectionStart),
        end: /** @type {number} */ (target.selectionEnd),
        direction: target.selectionDirection ?? "none",
      };
    } else if (saved) {
      target.setSelectionRange(saved.start, saved.end, saved.direction);
    }
  });
}
